import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import moment from "moment";
import api from "service/api";
import { toast } from "react-toastify";
import { NavLink } from "react-router-dom";
import PaginationComponent from "components/Pagination/Pagination";
import usePagination from "hooks/usepagination";
import Loading from "components/Loading";
import Select from "react-select";
import newApi from "service/newApi";
import { DateTool } from "utils/DateTool";
import { DatePicker } from "components/DatePicker";
import {
  Card,
  CardHeader,
  CardFooter,
  Table,
  Container,
  Row,
  Form,
  Button,
  Col,
  FormGroup,
  CardBody,
} from "reactstrap";
import { TextTool } from "utils/TextTool";
import { getExecutionStatusDescription } from "enum/pipeline-status";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Switch from "@mui/material/Switch";

const Pipelines = () => {
  const { register, handleSubmit, control, setValue, getValues } = useForm();
  const [info, setInfo] = useState([]);
  const { setActualPage, actualPage } = usePagination();
  const [offset, setOffset] = useState(actualPage || 1);
  const [onLoad, setOnLoad] = useState(false);
  const [clients, setClients] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    if (!clients.length) {
      setOnLoad(true);
      api
        .get(`/clients?page=1&limit=10000`)
        .then((response) => response.data)
        .then((response) => {
          setClients(
            response.clients.map((item) => ({
              label: item.name,
              value: item.id,
            })),
          );
          setOnLoad(false);
        })
        .catch(() => {
          toast.error("Erro ao recuperar clientes!");
          setOnLoad(false);
          return;
        });
    }
  }, []);

  const buildQuery = useCallback(() => {
    const limit = getValues("limit");
    const id = getValues("id");
    const title = getValues("title");
    const client = getValues("client");
    const created_start = getValues("created_start");
    const created_end = getValues("created_end");
    let query = `page=${offset}&limit=${limit}`;

    if (id) {
      if (!TextTool.validateUUID(id)) {
        toast.warn("ID Da pipeline precisa ser um UUID válido.");
        return null;
      }
      query += `&id=${id}`;
    }

    if (title) {
      query += `&title=${title}`;
    }

    if (client) {
      query += `&client=${client.value}`;
    }

    if ((created_start && !created_end) || (!created_start && created_end)) {
      toast.warn("Ambas as datas precisam ser selecionadas.");
      return null;
    }

    if (created_start && created_end) {
      if (created_end.isBefore(created_start)) {
        toast.warn("A data final deve ser posterior à data inicial.");
        return null;
      }

      const oneMonthLater = moment(created_start).add(1, "month");
      if (created_end.isAfter(oneMonthLater)) {
        toast.warn("O intervalo entre as datas deve ser inferior a 1 mês.");
        return null;
      }
      const startDateOnly = created_start.format("YYYY-MM-DD");
      const endDateOnly = created_end.format("YYYY-MM-DD");
      query += `&created_start=${startDateOnly}&created_end=${endDateOnly}`;
    }
    return query;
  }, [offset]);

  const submit = useCallback(
    (data) => {
      const query = buildQuery();
      if (!query) return;
      fetchRecords(query);
    },
    [offset],
  );

  const fetchRecords = (query) => {
    const startTime = moment();
    setInfo([]);
    setOnLoad(true);
    newApi
      .get(`admin/v1/pipeline?${query}`)
      .then((response) => {
        const endTime = moment();
        const requestDurationMs = endTime.diff(startTime);

        if (response.status === 204) {
          toast.info("Nenhuma pipeline encontrada para esta busca!");
          setOnLoad(false);
          return;
        }

        const total = response?.data?.total ?? 0;
        setTotalPages(Math.ceil(total / Number(getValues("limit"))));
        setTotalCount(total);
        const records = response.data.records;
        const successMessage =
          records.length === 1
            ? `1 pipeline foi retornada em ${DateTool.requestDurationFormat(
                requestDurationMs,
              )}!`
            : `${records.length} pipelines foram retornadas em ${DateTool.requestDurationFormat(
                requestDurationMs,
              )}`;
        toast.info(successMessage);
        setInfo(records ?? []);
        setOnLoad(false);
      })
      .catch(() => {
        toast.error("Erro ao recuperar as pipelines cadastradas!");
        setOnLoad(false);
        return;
      });
  };

  const clearSearch = () => {
    setValue("client", "", { shouldDirty: true });
    setValue("title", "", { shouldDirty: true });
    setValue("id", "", { shouldDirty: true });
    setValue("created_start", null, { shouldDirty: true });
    setValue("created_end", null, { shouldDirty: true });
  };

  const handleToggleRecurrence = useCallback(
    async (pipelineId, currentActive) => {
      const newActive = !currentActive;
      try {
        await newApi.patch(`admin/v1/pipeline/${pipelineId}/recurrence`, {
          active: newActive,
        });
        setInfo((prev) =>
          prev.map((item) =>
            item.id === pipelineId
              ? { ...item, recurrence_active: newActive }
              : item,
          ),
        );
        toast.success(
          newActive ? "Recorrência ativada." : "Recorrência pausada.",
        );
      } catch {
        toast.error("Erro ao alterar recorrência da pipeline.");
      }
    },
    [],
  );

  useEffect(() => {
    setActualPage(offset);
    if (offset !== actualPage) {
      const query = buildQuery();
      if (!query) return;
      fetchRecords(query);
    }
  }, [offset]);

  return (
    <>
      <Container className="mt-2" fluid>
        <div className="header bg-gradient-info pb-7 pt-2 pt-md-4 d-flex align-items-center justify-content-center flex-column ">
          <div className="container-fluid">
            <Form onSubmit={handleSubmit(submit)}>
              <Row>
                <Col sm={12} lg={4}>
                  <FormGroup>
                    <label className="text-white mr-2 ml-2">Cliente</label>
                    <Controller
                      name="client"
                      control={control}
                      render={({ field, fieldState }) => (
                        <>
                          <Select
                            {...field}
                            className="basic-single"
                            classNamePrefix="select"
                            placeholder="Selecione o cliente."
                            isSearchable={true}
                            isClearable={true}
                            options={clients}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            }}
                            onChange={(selectedOption) => {
                              field.onChange(selectedOption);
                            }}
                          />
                        </>
                      )}
                    />
                  </FormGroup>
                </Col>
                <Col sm={12} lg={4}>
                  <FormGroup>
                    <label className="text-white mr-2 ml-2">
                      Título da pipeline
                    </label>
                    <input
                      type="text"
                      className="form-control custom-input"
                      {...register("title")}
                    />
                  </FormGroup>
                </Col>
                <Col sm={12} lg={4}>
                  <FormGroup>
                    <label className="text-white mr-2 ml-2">
                      ID da pipeline
                    </label>
                    <input
                      type="text"
                      className="form-control custom-input"
                      {...register("id")}
                    />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col sm={12} md={6} lg={4}>
                  <label className="text-white mr-2 ml-2">Data Inicial</label>
                  <Controller
                    control={control}
                    name="created_start"
                    render={({ field: { onChange, value } }) => {
                      return (
                        <DatePicker
                          className="form-control"
                          viewMode="days"
                          value={value ?? null}
                          onChange={(date) => onChange(date)}
                          maxWidth={false}
                        />
                      );
                    }}
                  />
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <label className="text-white mr-2 ml-2">Data Final</label>
                  <Controller
                    control={control}
                    name="created_end"
                    render={({ field: { onChange, value } }) => (
                      <DatePicker
                        className="form-control"
                        viewMode="days"
                        value={value ?? null}
                        onChange={(date) => onChange(date)}
                        maxWidth={false}
                      />
                    )}
                  />
                </Col>
                <Col lg={2} className="justify-content-end d-flex flex-column">
                  <FormGroup>
                    <Card>
                      <Button
                        className="Button"
                        disabled={onLoad}
                        type="submit"
                        style={{ height: "40px" }}
                      >
                        Buscar
                      </Button>
                    </Card>
                  </FormGroup>
                </Col>
                <Col lg={2} className="justify-content-end d-flex flex-column ">
                  <FormGroup>
                    <Card>
                      <Button
                        className="Button"
                        disabled={onLoad}
                        onClick={clearSearch}
                        style={{ height: "40px" }}
                      >
                        Limpar
                      </Button>
                    </Card>
                  </FormGroup>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col sm={6} md={6} lg={2}>
                  <Card className="shadow-sm border-light">
                    <CardBody className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          className="text-muted text-uppercase mb-0"
                          style={{ fontSize: "0.8rem" }}
                        >
                          Pipelines
                        </span>
                        <span
                          className="mb-0 font-weight-semibold"
                          style={{ fontSize: "0.8rem" }}
                        >
                          {totalCount}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
                <Col sm={6} md={6} lg={2}>
                  <Card className="shadow-sm border-light">
                    <CardBody className="py-2 px-3">
                      <div className="d-flex align-items-center justify-content-between">
                        <span
                          className="text-muted text-uppercase mb-0"
                          style={{ fontSize: "0.8rem" }}
                        >
                          Páginas
                        </span>
                        <span
                          className="mb-0 font-weight-semibold"
                          style={{ fontSize: "0.8rem" }}
                        >
                          {totalPages}
                        </span>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Form>
          </div>
        </div>
      </Container>
      {/* Page content */}
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex flex-column ml-2 pl-2">
                <p className="m-0 pl-2"> Limite </p>
                <select
                  className="Input custom-select"
                  {...register("limit", {
                    onChange: () => {
                      const query = buildQuery();
                      fetchRecords(query);
                    },
                  })}
                >
                  <option value="10">10</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                {onLoad ? <Loading /> : ""}
                <thead className="thead-light">
                  <tr>
                    <th>Título</th>
                    <th>Cliente</th>
                    <th>Status da última execução</th>
                    <th>Registro</th>
                    <th>Recorrência</th>
                    <th>Execuções</th>
                    <th>Copiar</th>
                  </tr>
                </thead>
                <tbody>
                  {info.map((item) => (
                    <tr key={item.id}>
                      <td>{item?.title ?? "N/A"}</td>
                      <td>{item?.client_name ?? "N/A"}</td>
                      <td>
                        {getExecutionStatusDescription(
                          item?.last_execution?.status,
                        )}
                      </td>
                      <td>
                        {new Date(item.created_at).toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td>
                        {item.is_recurrent ? (
                          <Switch
                            checked={!!item.recurrence_active}
                            onChange={() =>
                              handleToggleRecurrence(
                                item.id,
                                item.recurrence_active,
                              )
                            }
                            size="small"
                            color="primary"
                          />
                        ) : (
                          <span
                            style={{ color: "#adb5bd", fontSize: "0.85em" }}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td>
                        <NavLink
                          to={`/admin/pipeline/${item.id}`}
                          activeClassName="active"
                          target="_blank"
                        >
                          <div
                            style={{
                              maxWidth: "70px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              textAlign: "center",
                            }}
                          >
                            <HistoryIcon />
                            <span
                              style={{ fontSize: "0.75em", marginTop: "4px" }}
                            >
                              Histórico
                            </span>
                          </div>
                        </NavLink>
                      </td>
                      <td>
                        <NavLink
                          to={`/admin/multi-step-pipeline-config?id=${item.id}`}
                          activeClassName="active"
                          target="_blank"
                        >
                          <div
                            style={{
                              maxWidth: "70px",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              textAlign: "center",
                            }}
                          >
                            <ContentCopyIcon />
                            <span
                              style={{ fontSize: "0.75em", marginTop: "4px" }}
                            >
                              Criar cópia
                            </span>
                          </div>
                        </NavLink>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <nav aria-label="...">
                  {info.length ? (
                    <p>Pipelines retornadas: {info.length}</p>
                  ) : (
                    <p>
                      Selecione ao menos um dos seguintes filtros para iniciar a
                      sua busca:
                      <br />
                      Cliente, ID da pipeline, Período de data
                      <br />
                      OBS: Período máximo de busca: 1 MÊS
                    </p>
                  )}
                </nav>
                <nav aria-label="...">
                  <PaginationComponent
                    limit={Number(getValues("limit"))}
                    offset={offset}
                    total={totalPages}
                    setOffset={setOffset}
                    maxReached={info.length < Number(getValues("limit"))}
                  />
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  );
};

export default Pipelines;
